
import { useState, useEffect } from 'react';
import { supabase, UpcomingEvent } from '../../../lib/supabase';
import UpcomingEventModal from './UpcomingEventModal';

export default function UpcomingEventsManager() {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<UpcomingEvent | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('upcoming_events')
      .select('*')
      .order('event_date', { ascending: true });
    setEvents(data || []);
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await supabase.from('upcoming_events').delete().eq('id', id);
    setEvents(prev => prev.filter(e => e.id !== id));
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const handleEdit = (event: UpcomingEvent) => {
    setEditingEvent(event);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditingEvent(null);
    setModalOpen(true);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return null;
    return timeStr.slice(0, 5);
  };

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white text-xl font-bold font-['Montserrat']">Próximos Eventos</h2>
          <p className="text-gray-500 text-sm mt-1">{events.length} evento{events.length !== 1 ? 's' : ''} programado{events.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={handleNew}
          className="flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-black font-bold text-sm px-5 py-2.5 rounded-lg transition-all shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.5)] cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Nuevo evento
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-gray-700">
            <i className="ri-calendar-line text-4xl"></i>
          </div>
          <p className="text-gray-500 text-sm">No hay eventos próximos.</p>
          <button
            onClick={handleNew}
            className="mt-4 text-cyan-400 hover:text-cyan-300 text-sm underline cursor-pointer"
          >
            Añadir el primero
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <div
              key={event.id}
              className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl p-5 flex items-center gap-5 transition-all group"
            >
              {/* Date badge */}
              <div className="flex-shrink-0 w-14 h-14 bg-black border border-cyan-400/30 rounded-xl flex flex-col items-center justify-center">
                <span className="text-cyan-400 text-lg font-black leading-none">
                  {new Date(event.event_date + 'T00:00:00').getDate()}
                </span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">
                  {new Date(event.event_date + 'T00:00:00').toLocaleDateString('es-ES', { month: 'short' })}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm truncate">{event.title}</h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                  <span className="flex items-center gap-1 text-gray-500 text-xs">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-map-pin-line text-xs"></i>
                    </div>
                    {event.location}{event.venue ? ` · ${event.venue}` : ''}
                  </span>
                  {formatTime(event.event_time) && (
                    <span className="flex items-center gap-1 text-gray-500 text-xs">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-time-line text-xs"></i>
                      </div>
                      {formatTime(event.event_time)}
                    </span>
                  )}
                  {event.ticket_url && (
                    <span className="flex items-center gap-1 text-cyan-500 text-xs">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-ticket-line text-xs"></i>
                      </div>
                      Con entradas
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {confirmDeleteId === event.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs whitespace-nowrap">¿Eliminar?</span>
                    <button
                      onClick={() => handleDelete(event.id)}
                      disabled={deletingId === event.id}
                      className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {deletingId === event.id ? '...' : 'Sí'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(event)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(event.id)}
                      className="w-8 h-8 flex items-center justify-center bg-gray-800 hover:bg-red-900/40 text-gray-400 hover:text-red-400 rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <UpcomingEventModal
          event={editingEvent}
          onClose={() => setModalOpen(false)}
          onSaved={fetchEvents}
        />
      )}
    </div>
  );
}
